/**
 * Leitura de um perfil de radiossonda — funções puras (testadas em
 * `perfil.test.ts`).
 *
 * Duas responsabilidades:
 *
 * 1. **Sincronia com o loop.** Cada quadro mostra a sondagem mais recente
 *    ATÉ aquele instante, nunca uma futura. O balão sobe duas vezes por dia,
 *    então o mapa troca de sondagem em 00Z e 12Z e fica parado no resto —
 *    pouco movimento, e é a verdade.
 * 2. **Geometria do gráfico.** Converter pressão em posição vertical, que é
 *    logarítmica porque a atmosfera é.
 */
import type { Indices, Nivel, Perfil } from './types';

/** Acima de quantas horas a sondagem deixa de representar o momento. */
export const IDADE_ANTIGA_H = 18;
/** Acima disto a estação é considerada calada. */
export const IDADE_AUSENTE_H = 48;

export type EstadoSondagem = 'recente' | 'antiga' | 'ausente' | 'erro';

/**
 * Instante em que a sondagem passa a existir para a animação: o LANÇAMENTO.
 *
 * Não é `ms`. O balão sobe uns 45 minutos ANTES da hora cheia, para que a
 * amostra da troposfera média caia nela — então `t` é negativo no começo da
 * trilha. Usar `ms` como marco fazia a estação ser pulada durante a primeira
 * metade do voo: medido, 27% a 66% da subida ficava invisível, e o balão
 * aparecia do nada a 8–15 km de altura com a trilha já inteira desenhada.
 */
export function inicioVisivel(perfil: Perfil | undefined): number | null {
  const janela = janelaDeVoo(perfil);
  if (janela) return janela.de;
  return perfil?.ms ?? null;
}

/**
 * Estado da estação no instante exibido.
 *
 * `instante` é o quadro do loop: uma sondagem que ainda não foi LANÇADA não
 * aconteceu do ponto de vista da animação, e mostrá-la seria mostrar o futuro.
 * A IDADE, porém, conta a partir de `ms`, que é o horário nominal pelo qual a
 * sondagem é identificada na meteorologia.
 */
export function estadoDe(perfil: Perfil | undefined, instante: number | null): EstadoSondagem {
  if (!perfil) return 'ausente';
  if (perfil.erro) return 'erro';
  if (perfil.ms === null || perfil.niveis.length === 0) return 'ausente';

  const ref = instante ?? Date.now();
  const inicio = inicioVisivel(perfil) ?? perfil.ms;
  if (inicio > ref) return 'ausente';

  const horas = (ref - perfil.ms) / 3_600_000;
  if (horas > IDADE_AUSENTE_H) return 'ausente';
  return horas > IDADE_ANTIGA_H ? 'antiga' : 'recente';
}

/** Idade da sondagem em horas no instante exibido; `null` se não há. */
export function idadeHoras(perfil: Perfil | undefined, instante: number | null): number | null {
  if (!perfil || perfil.ms === null) return null;
  const ref = instante ?? Date.now();
  if (perfil.ms > ref) return null;
  return (ref - perfil.ms) / 3_600_000;
}

// ---------------------------------------------------------------------------
// Escala de instabilidade (cor do balão)
// ---------------------------------------------------------------------------

/**
 * Faixas de CAPE, em J/kg. Os cortes seguem a leitura usual de instabilidade
 * convectiva: é o combustível disponível para tempestade, que na Amazônia é o
 * que importa.
 */
export const FAIXAS_CAPE: readonly { min: number; cor: string; rotulo: string }[] = [
  { min: 0, cor: '#5c7a8a', rotulo: 'estável' },
  { min: 300, cor: '#3fa9c4', rotulo: 'fraca' },
  { min: 1000, cor: '#3fbf6f', rotulo: 'moderada' },
  { min: 2000, cor: '#d9c53a', rotulo: 'forte' },
  { min: 3000, cor: '#e8803a', rotulo: 'muito forte' },
  { min: 4000, cor: '#d6455c', rotulo: 'extrema' },
];

export function faixaCape(cape: number): number {
  let i = 0;
  for (let k = 1; k < FAIXAS_CAPE.length; k++) {
    if (cape >= FAIXAS_CAPE[k]!.min) i = k;
    else break;
  }
  return i;
}

export function corCape(cape: number | undefined): string {
  return cape === undefined ? FAIXAS_CAPE[0]!.cor : FAIXAS_CAPE[faixaCape(cape)]!.cor;
}

// ---------------------------------------------------------------------------
// Geometria do gráfico
// ---------------------------------------------------------------------------

/** Faixa de pressão desenhada, em hPa (superfície → alta troposfera). */
export const P_BASE = 1000;
export const P_TOPO = 100;

/**
 * Posição vertical de uma pressão, de 0 (topo) a 1 (base).
 *
 * Logarítmica porque a atmosfera é: metade da massa está abaixo de 500 hPa, e
 * uma escala linear espremeria toda a troposfera média no meio do desenho.
 */
export function yDaPressao(p: number): number {
  const lo = Math.log(P_TOPO);
  const hi = Math.log(P_BASE);
  const k = (Math.log(Math.min(P_BASE, Math.max(P_TOPO, p))) - lo) / (hi - lo);
  return k;
}

/** Faixa de temperatura desenhada, em °C. */
export const T_MIN = -80;
export const T_MAX = 40;

/** Posição horizontal de uma temperatura, de 0 (fria) a 1 (quente). */
export function xDaTemperatura(t: number): number {
  return (Math.min(T_MAX, Math.max(T_MIN, t)) - T_MIN) / (T_MAX - T_MIN);
}

export interface Ponto {
  x: number;
  y: number;
}

/**
 * Pontos de uma curva (temperatura ou orvalho) em coordenadas 0..1.
 *
 * Níveis sem a medição pedida são PULADOS, e não interpolados: inventar um
 * ponto entre dois níveis distantes desenharia uma camada que ninguém mediu.
 */
export function curva(niveis: readonly Nivel[], campo: 't' | 'd'): Ponto[] {
  const pts: Ponto[] = [];
  for (const n of niveis) {
    const v = n[campo];
    if (v === null || !Number.isFinite(v)) continue;
    if (n.p > P_BASE || n.p < P_TOPO) continue;
    pts.push({ x: xDaTemperatura(v), y: yDaPressao(n.p) });
  }
  return pts;
}

/**
 * As três leituras do popup, em português claro.
 *
 * Índice ausente vira `null` e a linha some — melhor faltar que mostrar um
 * número inventado.
 */
export interface Leitura {
  rotulo: string;
  valor: string;
  dica: string;
}

export function leituras(indices: Indices): Leitura[] {
  const out: Leitura[] = [];

  if (Number.isFinite(indices.PWAT)) {
    out.push({
      rotulo: 'Água precipitável',
      valor: `${indices.PWAT!.toFixed(0)} mm`,
      dica: 'Quanta chuva o ar tem para dar, se toda a umidade da coluna precipitasse.',
    });
  }
  if (Number.isFinite(indices.MUCAPE)) {
    out.push({
      rotulo: 'Energia de convecção',
      valor: `${Math.round(indices.MUCAPE!)} J/kg`,
      dica: `Combustível de tempestade — ${FAIXAS_CAPE[faixaCape(indices.MUCAPE!)]!.rotulo}.`,
    });
  }
  const base = alturaDaBase(indices);
  if (base !== null) {
    out.push({
      rotulo: 'Base da nuvem',
      valor: `${base.toLocaleString('pt-BR', { maximumFractionDigits: 0 })} m`,
      dica: 'Altura em que a convecção começa a condensar.',
    });
  }
  return out;
}

/**
 * Altura aproximada do nível de condensação, a partir da pressão dele.
 *
 * Fórmula barométrica da atmosfera padrão — aproximação boa o bastante para
 * dizer "a nuvem começa perto de 600 m", que é o que a linha comunica.
 */
export function alturaDaBase(indices: Indices): number | null {
  const p = indices.LCLP;
  if (!Number.isFinite(p) || p! <= 0 || p! > 1100) return null;
  return 44330 * (1 - Math.pow(p! / 1013.25, 1 / 5.255));
}

// ---------------------------------------------------------------------------
// Trajetória do balão
// ---------------------------------------------------------------------------

/** Janela de voo de uma trilha: do primeiro ao último ponto medido. */
export function janelaDeVoo(perfil: Perfil | undefined): { de: number; ate: number } | null {
  if (!perfil?.trilha?.length || perfil.ms === null) return null;
  const com = perfil.trilha.filter((p) => p.t !== null);
  if (com.length < 2) return null;
  return {
    de: perfil.ms + com[0]!.t! * 1000,
    ate: perfil.ms + com[com.length - 1]!.t! * 1000,
  };
}

/**
 * Em que ponto da vida do voo está o quadro exibido.
 *
 * - `antes`: o balão ainda não subiu — não há posição nem trilha;
 * - `subindo`: está no ar, e a posição é interpolada entre pontos medidos;
 * - `final`: o voo acabou, e o balão fica onde parou.
 *
 * A fase `final` existe porque sem ela o balão sumia no fim do voo e só
 * restava o marcador da estação, que fica no COMEÇO da trilha — dava a
 * impressão de que a posição não batia com o percurso.
 */
export type FaseVoo = 'antes' | 'subindo' | 'final';

export interface PosicaoBalao {
  lon: number;
  lat: number;
  /** Altura em metros. */
  z: number;
  /** Fração do voo percorrida, de 0 a 1. */
  fracao: number;
  fase: FaseVoo;
}

/**
 * Onde o balão está no instante pedido.
 *
 * ANTES do lançamento devolve `null`: mostrar a sonda antes de ela subir seria
 * mostrar o futuro da animação.
 *
 * DURANTE o voo, interpola entre os pontos medidos — o balão anda pela trilha
 * conforme o loop avança e recua.
 *
 * DEPOIS do voo, para no ÚLTIMO ponto medido, que é onde o balão estourou.
 * Isso não é inventar posição: é a última posição que de fato existiu, e ela
 * continua valendo para todo quadro posterior. Sumir com o balão no fim
 * deixava só o marcador da estação, no começo da trilha, e parecia erro.
 */
export function posicaoNoInstante(perfil: Perfil | undefined, instante: number | null): PosicaoBalao | null {
  const janela = janelaDeVoo(perfil);
  if (!janela || instante === null || perfil?.ms === undefined || perfil.ms === null) return null;
  if (instante < janela.de) return null;

  const pts = perfil.trilha!.filter((p) => p.t !== null);
  const ultimo = pts[pts.length - 1]!;

  if (instante >= janela.ate) {
    return { lon: ultimo.x, lat: ultimo.y, z: ultimo.z, fracao: 1, fase: 'final' };
  }

  const alvo = (instante - perfil.ms) / 1000;
  for (let i = 1; i < pts.length; i++) {
    const a = pts[i - 1]!;
    const b = pts[i]!;
    if (alvo > b.t!) continue;
    const span = b.t! - a.t!;
    const k = span > 0 ? (alvo - a.t!) / span : 0;
    return {
      lon: a.x + (b.x - a.x) * k,
      lat: a.y + (b.y - a.y) * k,
      z: a.z + (b.z - a.z) * k,
      fracao: (instante - janela.de) / Math.max(1, janela.ate - janela.de),
      fase: 'subindo',
    };
  }
  return { lon: ultimo.x, lat: ultimo.y, z: ultimo.z, fracao: 1, fase: 'final' };
}

/**
 * Cor por ALTURA ao longo da trilha, para a subida se ler de relance.
 *
 * A rampa vai do quente perto do solo ao frio no topo, que é a intuição certa
 * — o ar esfria com a altura até a tropopausa. Em degraus, e não contínua,
 * porque degraus são testáveis e a diferença entre 14 km e 15 km não importa
 * para quem olha o mapa.
 */
export const FAIXAS_ALTURA: readonly { min: number; cor: string; rotulo: string }[] = [
  { min: 0, cor: '#ffd166', rotulo: 'superfície' },
  { min: 3000, cor: '#f4845f', rotulo: '3 km' },
  { min: 7000, cor: '#e05780', rotulo: '7 km' },
  { min: 12000, cor: '#a06cd5', rotulo: '12 km' },
  { min: 18000, cor: '#6a8ed8', rotulo: '18 km' },
  { min: 24000, cor: '#9be7ff', rotulo: '24 km' },
];

export function corAltura(z: number): string {
  let cor = FAIXAS_ALTURA[0]!.cor;
  for (const f of FAIXAS_ALTURA) if (z >= f.min) cor = f.cor;
  return cor;
}

/** Distância em km entre dois pontos, pela aproximação equirretangular. */
function distKm(a: { x: number; y: number }, b: { x: number; y: number }): number {
  const latMedia = ((a.y + b.y) / 2) * (Math.PI / 180);
  const dx = (b.x - a.x) * 111.32 * Math.cos(latMedia);
  const dy = (b.y - a.y) * 111.32;
  return Math.hypot(dx, dy);
}

/**
 * Quão longe da estação o balão terminou, em km.
 *
 * É a distância do lançamento ao ÚLTIMO ponto, e não a diagonal da caixa que
 * envolve o percurso: a caixa dizia "derivou 14 km" para um voo que terminou a
 * 3,4 km da estação, porque o balão foi e voltou.
 */
export function derivaKm(trilha: readonly { x: number; y: number }[] | null | undefined): number | null {
  if (!trilha?.length || trilha.length < 2) return null;
  return distKm(trilha[0]!, trilha[trilha.length - 1]!);
}

/** Comprimento do caminho percorrido, em km — o balão pode ir e voltar. */
export function percursoKm(trilha: readonly { x: number; y: number }[] | null | undefined): number | null {
  if (!trilha?.length || trilha.length < 2) return null;
  let total = 0;
  for (let i = 1; i < trilha.length; i++) total += distKm(trilha[i - 1]!, trilha[i]!);
  return total;
}

/** Altura máxima alcançada, em metros. */
export function tetoM(trilha: readonly { z: number }[] | null | undefined): number | null {
  return trilha?.length ? Math.max(...trilha.map((p) => p.z)) : null;
}

/**
 * Qual sondagem mostrar no instante exibido: a mais recente já LANÇADA até ali.
 *
 * Com uma lista por estação, o loop passa a trocar de sondagem ao cruzar cada
 * lançamento, em vez de ficar preso na última. `null` quando nenhuma foi
 * lançada ainda naquele quadro — mostrar a próxima seria mostrar o futuro.
 */
export function perfilNoInstante(
  perfis: readonly Perfil[] | undefined,
  instante: number | null,
): Perfil | undefined {
  if (!perfis?.length) return undefined;
  const ref = instante ?? Date.now();
  // A lista vem ordenada do mais recente para o mais antigo.
  for (const p of perfis) {
    const inicio = inicioVisivel(p);
    if (inicio !== null && inicio <= ref) return p;
  }
  // Nenhuma lançada até aqui: devolve a que tem erro, se houver, para o mapa
  // poder dizer "falhamos" em vez de fingir que a estação não existe.
  return perfis.find((p) => p.erro) ?? undefined;
}

/**
 * Onde o balão de fato deixou o solo, medido pelo GPS da sonda.
 *
 * O cadastro das estações vem da lista do IGRA, com coordenadas arredondadas:
 * em São Gabriel da Cachoeira ela está a **10,7 km** do lançamento real
 * (Santarém 9,1; Porto Velho 6,4). Desenhar o marco na coordenada cadastrada
 * o deixava visivelmente fora da trilha — parecia um segundo objeto solto.
 *
 * `null` quando não há trilha; aí a coordenada cadastrada é tudo o que existe.
 */
export function pontoDeLancamento(perfil: Perfil | undefined): { lon: number; lat: number } | null {
  const p = perfil?.trilha?.[0];
  return p ? { lon: p.x, lat: p.y } : null;
}

/**
 * Pressão na altura relativa `k` do gráfico (0 no topo, 1 na base) — o
 * inverso de `yDaPressao`, para o mouse virar leitura.
 */
export function pressaoNoY(k: number): number {
  const lo = Math.log(P_TOPO);
  const hi = Math.log(P_BASE);
  return Math.exp(lo + Math.min(1, Math.max(0, k)) * (hi - lo));
}

/**
 * Nível medido mais próximo de uma pressão, comparado em log(p).
 *
 * Em log porque é assim que a escala do gráfico funciona: a 150 hPa, 20 hPa de
 * diferença é meio gráfico; a 1000 hPa, é quase nada.
 */
export function nivelMaisProximo(niveis: readonly Nivel[], p: number): Nivel | null {
  let melhor: Nivel | null = null;
  let dist = Infinity;
  for (const n of niveis) {
    if (!(n.p > 0)) continue;
    const d = Math.abs(Math.log(n.p) - Math.log(p));
    if (d < dist) {
      dist = d;
      melhor = n;
    }
  }
  return melhor;
}

/**
 * Altura do nível mais alto que o gráfico chega a desenhar, em km.
 *
 * Não é a altura que o balão alcançou: ele continua subindo muito além do topo
 * do desenho (em Santarém, 26 km contra 16,7 do perfil). O gráfico para em
 * `P_TOPO` porque ali acaba a troposfera — no trópico o ponto mais frio fica
 * justamente perto de 100 hPa, e acima dele quase não há umidade, então as
 * duas curvas colam e param de dizer algo.
 *
 * Esticar o desenho até os 26 km custaria 41% da altura da troposfera, que é
 * onde o tempo acontece.
 */
export function topoDoPerfil(niveis: readonly Nivel[]): number | null {
  let maisAlto: number | null = null;
  for (const n of niveis) {
    if (!(n.p >= P_TOPO) || !Number.isFinite(n.z)) continue;
    if (maisAlto === null || n.z > maisAlto) maisAlto = n.z;
  }
  return maisAlto === null ? null : maisAlto / 1000;
}

/**
 * O que a ficha desenha no instante do player: com o balão ainda subindo, só
 * os níveis que ele já mediu (o gráfico vai se montando); depois do voo, o
 * perfil inteiro. `alturaDoBalao` é a marca do instante, em metros, só
 * durante a subida.
 */
export function niveisNoInstante(perfil: Perfil, instante: number | null): { niveis: readonly Nivel[]; alturaDoBalao: number | null } {
  const pos = posicaoNoInstante(perfil, instante);
  if (!pos || pos.fase !== 'subindo') return { niveis: perfil.niveis, alturaDoBalao: null };
  return { niveis: perfil.niveis.filter((n) => n.z <= pos.z), alturaDoBalao: pos.z };
}
